import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signInWithRedirect, getRedirectResult, signOut } from 'firebase/auth';
import { getUserRole, ensureDefaultAdminInFirestore } from '../utils/firebaseAuth';
import { UNAUTHORIZED_PORTAL_MESSAGE } from '../utils/portalAccess';
import { toastManager } from '../utils/toastManager';
import { AlertCircle, Lock } from 'lucide-react';
import './TechLogin.css';

function TechLogin() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const processSignedInUser = async (result) => {
    const userEmail = result?.user?.email;

    if (!userEmail) {
      await signOut(auth);
      throw new Error('Authentication failed. No email received.');
    }

    await ensureDefaultAdminInFirestore();
    const role = await getUserRole(userEmail);
    if (!role) {
      await signOut(auth);
      setError(UNAUTHORIZED_PORTAL_MESSAGE);
      toastManager.error(UNAUTHORIZED_PORTAL_MESSAGE);
      return;
    }

    toastManager.success('Successfully signed in!');
  };

  // Complete sign-in when returning from a redirect (used when popups are blocked,
  // e.g. inside embedded preview panes).
  useEffect(() => {
    let cancelled = false;
    getRedirectResult(auth)
      .then(async (result) => {
        if (cancelled || !result) return;
        setLoading(true);
        try {
          await processSignedInUser(result);
        } catch (error) {
          if (error.message?.includes('Unrecognized user')) return;
          console.error('Google Redirect Sign-In Error:', error);
          const errorMsg = error.message || 'Failed to sign in with Google. Please try again.';
          setError(errorMsg);
          toastManager.error(errorMsg);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Google Redirect Result Error:', error);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      toastManager.info('Signing in with Google...');
      const result = await signInWithPopup(auth, googleProvider);
      await processSignedInUser(result);
    } catch (error) {
      if (error.message?.includes('Unrecognized user')) return;

      // Popups are blocked in embedded/preview browsers. Fall back to full-page redirect.
      const popupUnavailable = [
        'auth/popup-blocked',
        'auth/operation-not-supported-in-this-environment',
        'auth/cancelled-popup-request',
      ].includes(error.code);

      if (popupUnavailable) {
        try {
          toastManager.info('Popup blocked — redirecting to Google sign-in...');
          await signInWithRedirect(auth, googleProvider);
          return; // Page navigates away; result handled on return.
        } catch (redirectError) {
          console.error('Google Redirect Sign-In Error:', redirectError);
          const redirectMsg = redirectError.message || 'Failed to sign in with Google. Please try again.';
          setError(redirectMsg);
          toastManager.error(redirectMsg);
          setLoading(false);
          return;
        }
      }

      console.error('Google Sign-In Error:', error);
      const errorMsg = error.code === 'auth/popup-closed-by-user' 
        ? 'Sign-in was cancelled. Please try again.'
        : error.message || 'Failed to sign in with Google. Please try again.';
      setError(errorMsg);
      toastManager.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tech-login-container login-unified">
      {/* Single full-stage motion background */}
      <div className="aurora-canvas" aria-hidden="true">
        <div className="aurora-veil" />
        <div className="aurora-orb aurora-orb-a" />
        <div className="aurora-orb aurora-orb-b" />
        <div className="aurora-orb aurora-orb-c" />
        <div className="aurora-orb aurora-orb-d" />
        <div className="aurora-orb aurora-orb-e" />
        <div className="aurora-mesh" />
        <div className="orbit-ring orbit-ring-1" />
        <div className="orbit-ring orbit-ring-2" />
        <div className="orbit-ring orbit-ring-3" />
        <div className="aurora-beams">
          <span className="aurora-beam b1" />
          <span className="aurora-beam b2" />
          <span className="aurora-beam b3" />
        </div>
        <div className="signal-nodes">
          <span className="signal-node n1" />
          <span className="signal-node n2" />
          <span className="signal-node n3" />
          <span className="signal-node n4" />
          <span className="signal-node n5" />
        </div>
        <div className="aurora-particles">
          {[
            ['7%', '13s', '-2s'],
            ['14%', '16s', '-8s'],
            ['22%', '11s', '-4s'],
            ['31%', '18s', '-11s'],
            ['39%', '12s', '-1s'],
            ['48%', '15s', '-9s'],
            ['56%', '10s', '-5s'],
            ['64%', '17s', '-3s'],
            ['71%', '13s', '-12s'],
            ['79%', '14s', '-6s'],
            ['86%', '19s', '-7s'],
            ['93%', '12s', '-10s'],
          ].map(([x, dur, delay]) => (
            <span key={x} style={{ '--x': x, '--dur': dur, '--delay': delay }} />
          ))}
        </div>
        <svg className="pulse-wave" viewBox="0 0 600 120" preserveAspectRatio="none">
          <path
            className="pulse-path"
            d="M0 60 H80 L100 60 L120 20 L140 100 L160 60 H240 L260 60 L280 35 L300 85 L320 60 H400 L420 60 L440 15 L460 105 L480 60 H600"
          />
        </svg>
      </div>

      <div className="login-stage">
        <div className="login-card">
          <div className="login-hero">
            <div className="logo-container">
              <img src="/diologic-logo.png" alt="DIOLOGIC Logo" className="diologic-logo-image" />
            </div>
          </div>

          <div className="form-section">
            <h2 className="login-form-title">Sign in</h2>
            <p className="login-form-hint">Use your Diologic account to continue.</p>
            <form
              className="login-form"
              onSubmit={(e) => e.preventDefault()}
              autoComplete="off"
            >
              {error && (
                <div className="error-message">
                  <AlertCircle className="error-icon" size={18} />
                  {error}
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="login-email">Username / Email</label>
                <input
                  id="login-email"
                  type="email"
                  className="form-input"
                  placeholder="Enter your email"
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="login-password">Password</label>
                <input
                  id="login-password"
                  type="password"
                  className="form-input"
                  placeholder="Enter your password"
                  autoComplete="off"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="primary-btn"
                >
                  Sign In
                </button>
              </div>

              <div className="login-divider">
                <span>or</span>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className={`google-btn ${loading ? 'disabled' : ''}`}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Signing In...
                    </>
                  ) : (
                    <>
                      <div className="google-icon">
                        <svg viewBox="0 0 48 48" width="18" height="18">
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.6-6.6C34.44 3.15 28.76 1 24 1 14.62 1 6.51 5.85 2.56 13.83l7.44 5.68C13.97 13.21 18.54 9.5 24 9.5z"/>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 40.15 14.62 45 24 45z"/>
                        </svg>
                      </div>
                      Sign in with Google
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="security-section">
              <div className="security-badge">
                <Lock className="security-icon" size={20} />
                <div className="security-text">
                  <span className="security-title">Secure Authentication</span>
                  <span className="security-desc">SSL encrypted • Two-factor ready</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TechLogin;